import { useEffect, useMemo, useRef, useState } from 'react';
import { Tabs, Tab, Input } from '@heroui/react';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ISearchableTabsProps<T, P extends {} = {}> {
	/**
	 * A React component used to render the content of each tab.
	 * This component will receive props generated by `dynamicItemProps`.
	 */
	dynamicItem?: React.FC<P>;

	/**
	 * A function that takes an item from the `dynamicItemData` array
	 * and returns the props to be passed to the `dynamicItem` component.
	 * If not provided, the item itself will be passed as props.
	 */
	dynamicItemProps?: (item: T) => P;

	/**
	 * An array of data objects used to render the tabs.
	 * Each object should have a `name` property to be used as the tab title.
	 */
	dynamicItemData?: T[];

	/**
	 * The color of the tabs. Defaults to an empty string if not provided.
	 */
	tabColor?: string;

	/**
	 * The text color of the tabs. Defaults to an empty string if not provided.
	 */
	tabTextColor?: string;
}

const SearchableTabs = <T, P extends {} = {}>({
	tabColor = '',
	dynamicItem: DynamicItemComponent,
	dynamicItemProps,
	dynamicItemData,
}: ISearchableTabsProps<T, P>) => {
	const [search, setSearch] = useState('');

	const [tabListWidth, setTabListWidth] = useState(0);

	if (!dynamicItemData || dynamicItemData.length === 0) {
		return <div className="w-full h-full flex items-center justify-center">No data available</div>;
	}

	// Filter the tabs based on the search query.
	const filteredTabs = dynamicItemData!.filter((tab: any) => tab.name.toLowerCase().includes(search.toLowerCase()));

	const tabRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const updateWidth = () => {
			if (tabRef.current) {
				setTabListWidth(tabRef.current.offsetWidth);
			}
		};

		// Initial width calculation.
		updateWidth();

		// Observe changes to the tab list container's size.
		const resizeObserver = new ResizeObserver(updateWidth);
		if (tabRef.current) {
			resizeObserver.observe(tabRef.current);
		}

		// Listen for window resize events to update the width.
		window.addEventListener('resize', updateWidth);

		// Cleanup the observer and event listener on component unmount.
		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', updateWidth);
		};
	}, []);

	// Calculate the style for the search box based on the tab list width.
	const serachBoxStyle = useMemo(() => {
		return {
			width: tabListWidth < 144 ? 144 : tabListWidth - 8,
			left: 16,
		};
	}, [tabListWidth]);

	return (
		<div className="w-full h-full">
			{/* Search box positioned above the tabs */}
			<div className="absolute z-50" style={serachBoxStyle}>
				<Input
					isClearable={true}
					onClear={() => setSearch('')}
					classNames={{
						inputWrapper: `text-default-500 rounded-none`,
						clearButton: 'text-lg flex items-center',
					}}
					placeholder={'Search'}
					size="sm"
					startContent={<FontAwesomeIcon icon={faSearch} />}
					type="text"
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>

			<Tabs
				ref={tabRef}
				variant="underlined"
				classNames={{
					tabList: `flex gap-1 rounded-none bg-msi-white-color pt-10`,
					tab: `rounded-none border-none data-[selected=true]:opacity-100 opacity-60 bg-${tabColor || 'msi-chart-midblue'}`,
					tabContent: `!text-msi-white-color`,
					cursor: `rounded-none bg-${tabColor || 'msi-white-color'}`,
					panel: 'flex flex-auto w-full overflow-auto',
					base: 'h-full border-r border-msi-darkgrey-color-300 min-w-40  justify-center overflow-auto',
				}}
				isVertical
			>
				{filteredTabs?.map((item: any, index) => {
					// Generate props for the dynamic item component if a function is provided.
					const props = dynamicItemProps ? dynamicItemProps(item) : ({} as P);
					return (
						<Tab key={index.toString()} title={`${item.name}`}>
							{/* Render the dynamic item component or fallback to displaying the item as JSON */}
							{DynamicItemComponent ? <DynamicItemComponent {...props} /> : <div>{JSON.stringify(item)}</div>}
						</Tab>
					);
				})}
			</Tabs>
		</div>
	);
};

export default SearchableTabs;
